<script setup lang="ts">
import { AxisItem } from "@/modules/explorer/components/AxisConfiguration/Selection.vue";
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import { useSegmentStore } from "@/modules/explorer/SegmentStore";
import {
	verticalHasSegmentOption,
	verticalOrDimensionSupportsLostCustomers,
} from "@/modules/explorer/PropertyStore";
import { toRaw } from "vue";

const props = withDefaults(
	defineProps<{
		item: AxisItem;
		showDescriptionAsText?: boolean;
		isAvailable?: boolean;
		defaultSegmentId?: number;
		modelValue: any;
		makeCurrentDisabled?: boolean;
	}>(),
	{
		showDescriptionAsText: false,
		isAvailable: true,
		modelValue: null,
		makeCurrentDisabled: true,
	}
);

function valueComparator(a: any, b: any) {
	const rawB = toRaw(b);

	if (Array.isArray(a)) {
		return a.some((item) => item?.id === rawB?.id);
	}

	return a?.id === rawB?.id;
}

const stripLocalizedFields = (
	inputObj: AxisItem
): DocumentDimensionDTO | DocumentVerticalDTO => {
	// if hierarchy, set default hierarchy id
	if (inputObj.type === "hierarchy") {
		inputObj["hierarchy_definition_id"] =
			inputObj?.hierarchy_definitions.find(
				(hierarchyDefinition) => hierarchyDefinition.is_default
			)?.id ?? null;
	}

	// if bucketed, set default bucket id
	if (inputObj.type === "bucketed") {
		inputObj["dimension_bucket_id"] =
			inputObj?.dimension_buckets.find(
				(bucketDefinition) => bucketDefinition.is_default
			)?.id ?? null;
	}

	// if bucketed, set default segment id
	if (
		inputObj.type === "segment" ||
		verticalHasSegmentOption(inputObj.name_clean)
	) {
		const availableSegments = verticalOrDimensionSupportsLostCustomers(
			inputObj.name_clean
		)
			? useSegmentStore().segments
			: useSegmentStore().segments.filter(
					(segment) => !segment.has_dependency_on_lost_customer_segment
			  );
		inputObj["segment_id"] =
			props.defaultSegmentId ?? availableSegments[0]?.id ?? null;
	}

	delete inputObj["localizedDescription"];
	delete inputObj["localizedTitle"];
	delete inputObj["hierarchy_definitions"];
	delete inputObj["dimension_buckets"];

	return inputObj;
};

function isChecked(item: AxisItem) {
	const model = toRaw(props.modelValue);
	if (Array.isArray(model)) {
		return model.some((i) => i?.id === item?.id);
	}
	return model?.id === item?.id;
}
</script>

<template>
	<div
		class="axis-item"
		:class="{ 'axis-item--selected': isChecked(props.item) }"
	>
		<v-radio
			:value="stripLocalizedFields({ ...props.item })"
			:value-comparator="valueComparator"
			:disabled="!isAvailable || (makeCurrentDisabled && isChecked(props.item))"
		>
			<template v-slot:label>
				<v-tooltip :text="props.item.localizedDescription" location="end">
					<template v-slot:activator="slot">
						<span v-bind="slot.props" class="axis-item__label">
							<span class="axis-item__title">
								{{ props.item.localizedTitle }}
							</span>
							<span
								v-if="props.showDescriptionAsText"
								class="axis-item__description text-caption"
							>
								{{ props.item.localizedDescription }}
							</span>
						</span>
					</template>
				</v-tooltip>
			</template>
		</v-radio>
		<div v-if="$slots.specification" class="axis-item__specification">
			<slot name="specification" />
		</div>
	</div>
</template>

<style scoped lang="scss">
/* Rows follow the FilterDialog grammar: one line, the title ellipsised before
   the muted secondary text, and the specification pushed to the trailing edge
   so the list keeps a single left rhythm. */
.axis-item {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;
	border-radius: 4px;

	&:hover {
		background: rgb(var(--color-secondary));
	}
}

.axis-item__label {
	display: flex;
	align-items: center;
	gap: 0.375rem;
	min-width: 0;
}

.axis-item__title {
	flex: 0 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.axis-item--selected .axis-item__title {
	font-weight: 600;
}

.axis-item__description {
	/* A zero basis keeps the title at its natural width and spends whatever is
	   left on the description, so the title is the last thing to be clipped. */
	flex: 1 1 0;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: rgb(var(--color-on-background));
}

.axis-item__specification {
	flex: 0 0 auto;
	margin-inline-start: auto;
	padding-inline-end: 0.25rem;
}
</style>
